import React, { useState, useEffect, useCallback, useId, useRef } from 'react';
import { toast } from 'react-toastify';
import { Field, focusablesIn } from './Modal';
import TabBar, { tabPanelProps } from './TabBar';
import ErrorState from './ErrorState';
import Loading from './Loading';
import { Chip } from './StatusChip';
import { useConfirm } from './ConfirmDialog';
import { apiFetch, apiErrorMessage } from '../hooks/useApi';
import { formatDate, formatDateTime } from '../utils/format';
import {
  TASK_STATUSES, TASK_TYPES, SAFETY_MODULES, TASK_SOURCES,
  taskFlags, statusMetaFor,
} from '../utils/taskMeta';

/**
 * <TaskDetailPanel /> — slides in from the right so the list stays put behind
 * it. Everything here writes straight through to the task, and the parent
 * refreshes its row when something changes. It behaves as a modal dialog:
 * focus moves in on open, Escape closes it (unless a confirm is open on top),
 * and focus returns to whatever opened it.
 */

const label = { fontSize: 'var(--cs-text-xs)', fontWeight: 600, color: 'var(--cw-text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em' };
const value = { fontSize: 'var(--cs-text-base)', color: 'var(--cw-text)', marginTop: 3 };
const muted = { color: 'var(--cw-text-faint)', fontSize: 'var(--cs-text-sm)' };

const TABS = [{ id: 'detail', label: 'Detail' }, { id: 'comments', label: 'Comments' }, { id: 'history', label: 'History' }];

const numOrBlank = (v) => v === '' || v == null || (Number.isFinite(Number(v)) && Number(v) >= 0);

const TaskDetailPanel = ({ taskId, assignees = [], onClose, onChanged }) => {
  const { prompt } = useConfirm();
  const titleId = `cw-task-${useId().replace(/:/g, '')}`;
  const panelRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [tab, setTab] = useState('detail');
  const [comment, setComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiFetch(`/api/contractor/tasks/${taskId}`);
      setData(d || null);
      setLoadError(null);
    } catch (e) { setLoadError(e); }
    finally { setLoading(false); }
  }, [taskId]);

  useEffect(() => { load(); }, [load]);

  // Focus in on open; return focus to the opener on close.
  useEffect(() => {
    const opener = document.activeElement;
    if (panelRef.current) panelRef.current.focus();
    return () => { if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus(); };
  }, []);

  // Escape closes the panel and Tab is trapped inside it — but not while a
  // confirm/prompt dialog (the Modal stack, z-index 1300+) is open over it.
  useEffect(() => {
    const onKey = (e) => {
      if (document.querySelector('.cw-modal-backdrop')) return;
      if (e.key === 'Escape') {
        if (e.defaultPrevented) return;
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = panelRef.current;
      if (!root) return;
      const items = focusablesIn(root);
      if (items.length === 0) { e.preventDefault(); root.focus(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!root.contains(active)) { e.preventDefault(); first.focus(); return; }
      if (e.shiftKey && (active === first || active === root)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const patch = async (body, msg) => {
    setSaving(true);
    try {
      await apiFetch(`/api/contractor/tasks/${taskId}`, { method: 'PUT', body: JSON.stringify(body) });
      if (msg) toast.success(msg);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Couldn’t save that change'));
      await load();   // put the field back to what the server holds
    } finally { setSaving(false); }
  };

  const block = async () => {
    const reason = await prompt({
      title: 'Mark this task blocked?',
      body: 'Say what is stopping it, so whoever picks it up knows what to unblock.',
      label: 'Reason',
      inputType: 'textarea',
      confirmLabel: 'Mark blocked',
    });
    if (reason != null) patch({ status: 'blocked', blocked_reason: reason.trim() }, 'Marked blocked');
  };

  const saveHours = (key, v) => {
    if (!numOrBlank(v)) { setErrors((x) => ({ ...x, [key]: 'Enter a number of hours, e.g. 1.5' })); return; }
    setErrors((x) => ({ ...x, [key]: undefined }));
    patch({ [key]: v });
  };

  const addComment = async () => {
    if (!comment.trim() || posting) return;
    setPosting(true);
    try {
      await apiFetch(`/api/contractor/tasks/${taskId}/comments`, { method: 'POST', body: JSON.stringify({ body: comment }) });
      setComment('');
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, 'Couldn’t post that comment')); }
    finally { setPosting(false); }
  };

  const t = data?.task;
  const flags = t ? taskFlags(t) : [];
  const editLocal = (key) => (e) => setData((d) => ({ ...d, task: { ...d.task, [key]: e.target.value } }));

  const renderBody = () => {
    if (loading && !t) return <Loading label="Loading task" />;
    if (loadError && !t) return <ErrorState thing="this task" error={loadError} onRetry={load} compact />;
    if (!t) return <ErrorState thing="this task" reason="This task couldn’t be found — it may have been deleted." compact />;

    if (tab === 'detail') {
      const st = statusMetaFor(t.status);
      return (
        <div style={{ display: 'grid', gap: 16 }}>
          {/* Quick actions — the things done most often, one click each. */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {t.status !== 'done' ? (
              <button type="button" className="cw-btn cw-btn-primary" disabled={saving}
                onClick={() => patch({ status: 'done' }, 'Marked done')}>
                <i className="fa-solid fa-check" aria-hidden="true" /> Mark done
              </button>
            ) : (
              <button type="button" className="cw-btn cw-btn-secondary" disabled={saving}
                onClick={() => patch({ status: 'in_progress' }, 'Reopened')}>
                <i className="fa-solid fa-rotate-left" aria-hidden="true" /> Reopen
              </button>
            )}
            {t.status === 'not_started' && (
              <button type="button" className="cw-btn cw-btn-secondary" disabled={saving}
                onClick={() => patch({ status: 'in_progress' }, 'Started')}>Start</button>
            )}
            {t.status !== 'blocked' && t.status !== 'done' && (
              <button type="button" className="cw-btn cw-btn-secondary" disabled={saving} onClick={block}>Block</button>
            )}
          </div>

          {t.status === 'blocked' && t.blocked_reason && (
            <div role="note" style={{ fontSize: 'var(--cs-text-sm)', color: 'var(--cw-danger-fg)', background: 'var(--cw-danger-tint)', border: '1px solid var(--cw-danger-border)', borderRadius: 8, padding: '9px 11px' }}>
              <i className="fa-solid fa-ban" style={{ marginRight: 6 }} aria-hidden="true" /><strong>Blocked:</strong> {t.blocked_reason}
            </div>
          )}

          {t.awaiting_permit && (
            <div role="note" style={{ fontSize: 'var(--cs-text-sm)', color: 'var(--cw-warning-fg)', background: 'var(--cw-warning-tint)', border: '1px solid var(--cw-warning-border)', borderRadius: 8, padding: '9px 11px' }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 6 }} aria-hidden="true" />
              A permit is required and none has been issued. Sort this before anyone travels to site.
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 12 }}>
            <Field label="Status">
              <select value={st.key} onChange={(e) => patch({ status: e.target.value })} disabled={saving}>
                {!TASK_STATUSES.some((s) => s.key === st.key) && <option value={st.key}>{st.label}</option>}
                {TASK_STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Assignee">
              <select value={t.assignee_id || ''} disabled={saving}
                onChange={(e) => patch({ assignee_id: e.target.value || null }, 'Reassigned')}>
                <option value="">Unassigned</option>
                {assignees.map((a) => <option key={a.id} value={a.id}>{a.name || a.email}</option>)}
              </select>
            </Field>
            <Field label="Due date">
              <input type="date" value={t.due_date ? String(t.due_date).slice(0, 10) : ''} disabled={saving}
                onChange={(e) => patch({ due_date: e.target.value || null }, 'Rescheduled')} />
            </Field>
            <Field label="Start date">
              <input type="date" value={t.start_date ? String(t.start_date).slice(0, 10) : ''} disabled={saving}
                onChange={(e) => patch({ start_date: e.target.value || null })} />
            </Field>
            <Field label="Type">
              <select value={t.task_type || 'admin'} onChange={(e) => patch({ task_type: e.target.value })} disabled={saving}>
                {TASK_TYPES.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
              </select>
            </Field>
            <Field label="Safety module">
              <select value={t.safety_module || ''} onChange={(e) => patch({ safety_module: e.target.value })} disabled={saving}>
                <option value="">None</option>
                {SAFETY_MODULES.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
              </select>
            </Field>
            <Field label="Estimated hours" error={errors.estimated_hours}>
              <input value={t.estimated_hours ?? ''} inputMode="decimal"
                onBlur={(e) => saveHours('estimated_hours', e.target.value)} onChange={editLocal('estimated_hours')} />
            </Field>
            <Field label="Logged hours" error={errors.logged_hours}>
              <input value={t.logged_hours ?? ''} inputMode="decimal"
                onBlur={(e) => saveHours('logged_hours', e.target.value)} onChange={editLocal('logged_hours')} />
            </Field>
          </div>

          <Field label="Description">
            <textarea rows={3} style={{ resize: 'vertical' }} value={t.description || ''}
              onBlur={(e) => patch({ description: e.target.value })} onChange={editLocal('description')} />
          </Field>

          <div style={{ borderTop: '1px solid var(--cw-border-light)', paddingTop: 14, display: 'grid', gap: 10 }}>
            <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 12, margin: 0 }}>
              <div><dt style={label}>Job</dt><dd style={{ ...value, margin: 0 }}>{t.job_number ? `${t.job_number}${t.job_title ? ` — ${t.job_title}` : ''}` : '—'}</dd></div>
              <div><dt style={label}>Client</dt><dd style={{ ...value, margin: 0 }}>{t.client_name || '—'}</dd></div>
              <div><dt style={label}>Property</dt><dd style={{ ...value, margin: 0 }}>{t.property_name || '—'}</dd></div>
              <div>
                <dt style={label}>SLA deadline</dt>
                <dd style={{ ...value, margin: 0, color: t.sla_breached ? 'var(--cw-danger-fg)' : 'var(--cw-text)' }}>
                  {t.sla_due_at ? formatDateTime(t.sla_due_at) : '—'}{t.sla_breached ? ' (breached)' : ''}
                </dd>
              </div>
              <div><dt style={label}>Priority</dt><dd style={{ ...value, margin: 0 }}>{t.priority_code || '—'}</dd></div>
              <div><dt style={label}>Source</dt><dd style={{ ...value, margin: 0 }}>{(TASK_SOURCES.find((s) => s.key === t.source) || {}).label || t.source || '—'}</dd></div>
            </dl>
            {t.completed_at && (
              <p style={{ ...muted, margin: 0 }}>
                Completed {formatDateTime(t.completed_at)}{t.completed_by_name ? ` by ${t.completed_by_name}` : ''}
              </p>
            )}
          </div>

          {data.dependencies?.length > 0 && (
            <div style={{ borderTop: '1px solid var(--cw-border-light)', paddingTop: 14 }}>
              <h3 style={{ ...label, margin: '0 0 8px' }}>Waiting on</h3>
              <ul style={{ display: 'grid', gap: 6, margin: 0, padding: 0, listStyle: 'none' }}>
                {data.dependencies.map((d) => {
                  const ds = statusMetaFor(d.status);
                  return (
                    <li key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--cs-text-sm)' }}>
                      <Chip bg={ds.bg} fg={ds.fg} border={ds.border} label={ds.label} />
                      <span style={{ color: 'var(--cw-text)' }}>{d.title}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      );
    }

    if (tab === 'comments') {
      return (
        <div style={{ display: 'grid', gap: 12 }}>
          <form style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }} onSubmit={(e) => { e.preventDefault(); addComment(); }}>
            <Field label="Add a comment" style={{ flex: 1, marginBottom: 0 }}>
              <input value={comment} placeholder="Write a comment…" onChange={(e) => setComment(e.target.value)} />
            </Field>
            <button type="submit" className="cw-btn cw-btn-primary" disabled={!comment.trim() || posting}>{posting ? 'Posting…' : 'Post'}</button>
          </form>
          {(data.comments || []).length === 0 ? (
            <p style={{ ...muted, padding: '18px 0', textAlign: 'center', margin: 0 }}>No comments yet.</p>
          ) : (
            data.comments.map((c) => (
              <div key={c.id} style={{ border: '1px solid var(--cw-border)', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ ...muted, fontSize: 'var(--cs-text-xs)', marginBottom: 4 }}>
                  {c.author_name || 'Someone'} · {formatDateTime(c.created_at)}
                </div>
                <div style={{ fontSize: 'var(--cs-text-base)', color: 'var(--cw-text)', whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>{c.body}</div>
              </div>
            ))
          )}
        </div>
      );
    }

    return (
      <div style={{ display: 'grid', gap: 8 }}>
        {(data.audit || []).length === 0 ? (
          <p style={{ ...muted, padding: '18px 0', textAlign: 'center', margin: 0 }}>Nothing changed yet.</p>
        ) : (
          data.audit.map((a) => (
            <div key={a.id} style={{ display: 'flex', gap: 10, fontSize: 'var(--cs-text-sm)', borderBottom: '1px solid var(--cw-border-light)', paddingBottom: 8 }}>
              <div style={{ ...muted, whiteSpace: 'nowrap', fontSize: 'var(--cs-text-xs)', paddingTop: 2 }}>{formatDate(a.created_at)}</div>
              <div style={{ color: 'var(--cw-text-2)' }}>
                <strong style={{ color: 'var(--cw-text)' }}>{String(a.field_key || '').replace(/_/g, ' ')}</strong>
                {' '}changed{a.old_value ? ` from “${a.old_value}”` : ''}{a.new_value ? ` to “${a.new_value}”` : ''}
                {a.changed_by_name ? ` · ${a.changed_by_name}` : ''}
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <>
      {/* Backdrop: mouse convenience only (Escape and the close button are the accessible routes). */}
      <div aria-hidden="true" onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.35)', zIndex: 1200 }} />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(560px, 100vw)', background: 'var(--cw-surface)',
                 boxShadow: '-8px 0 30px rgba(0,0,0,0.14)', zIndex: 1201, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--cw-border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ ...muted, fontSize: 'var(--cs-text-xs)', marginBottom: 3 }}>
                Task #{taskId}{t?.job_number ? ` · ${t.job_number}` : ''}
              </div>
              <h2 id={titleId} style={{ fontWeight: 700, color: 'var(--cw-text)', fontSize: 'var(--cs-text-lg)', lineHeight: 1.3, margin: 0 }}>
                {loading && !t ? 'Loading task…' : (t?.title || 'Task')}
              </h2>
              {t && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                  {(() => { const st = statusMetaFor(t.status); return <Chip bg={st.bg} fg={st.fg} border={st.border} label={st.label} dot />; })()}
                  {flags.map((f) => <Chip key={f.key} bg={f.bg} fg={f.fg} border={f.border} label={f.label} />)}
                </div>
              )}
            </div>
            <button type="button" className="cw-icon-btn" onClick={onClose} aria-label="Close task details">
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </button>
          </div>

          <TabBar
            tabs={TABS.map((x) => (x.id === 'comments' && data?.comments?.length ? { ...x, count: data.comments.length } : x))}
            active={tab}
            onChange={setTab}
            idBase={`${titleId}-tabs`}
            ariaLabel="Task sections"
            style={{ marginTop: 12 }}
          />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px' }} {...tabPanelProps(`${titleId}-tabs`, tab)}>
          {renderBody()}
        </div>
      </aside>
    </>
  );
};

export default TaskDetailPanel;
